import React, { useState, useEffect } from 'react';
import {
  Container,
  Header,
  Table,
  Button,
  SpaceBetween,
  Badge,
  Box,
  Pagination,
  TextFilter,
  Alert
} from '@cloudscape-design/components';
import { dynamodb, config } from '../aws-config';

function DocumentList({ onSelectDocument }) {
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedItems, setSelectedItems] = useState([]);
  const [filteringText, setFilteringText] = useState('');
  const [currentPageIndex, setCurrentPageIndex] = useState(1);
  const [alert, setAlert] = useState(null);

  const pageSize = 10;

  useEffect(() => {
    loadDocuments();
  }, []);

  const loadDocuments = async () => {
    setLoading(true);
    try {
      console.log('Loading submissions from:', config.submissionsTable);
      
      // Load only submissions
      const result = await dynamodb.scan({ 
        TableName: config.submissionsTable, 
        Limit: 50 
      }).promise();

      console.log('Submissions result:', result);

      const submissions = (result.Items || []).map(item => ({
        ...item,
        displayName: item.s3Key?.split('/').pop() || item.submissionId || 'Unknown',
        status: item.processingStatus || (item.isFormComplete ? 'Complete' : 'Incomplete')
      }));

      console.log('Processed submissions:', submissions);

      setDocuments(submissions);
    } catch (error) {
      console.error('Error loading documents:', error);
      setAlert({ type: 'error', content: `Failed to load documents: ${error.message}` });
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    // Normalize status to uppercase for consistent matching
    const normalizedStatus = status?.toString().toUpperCase();
    
    const statusConfig = {
      UPLOADED: { 
        backgroundColor: '#0972d3', 
        color: '#ffffff',
        text: 'Uploaded' 
      },
      PROCESSING: { 
        backgroundColor: '#0972d3', 
        color: '#ffffff',
        text: 'Processing' 
      },
      APPROVED: { 
        backgroundColor: '#037f0c', 
        color: '#ffffff',
        text: 'Approved' 
      },
      NEEDS_REVIEW: { 
        backgroundColor: '#f89256', 
        color: '#000000',
        text: 'Needs Review' 
      },
      REJECTED: { 
        backgroundColor: '#d91515', 
        color: '#ffffff',
        text: 'Rejected' 
      },
      ERROR: { 
        backgroundColor: '#d91515', 
        color: '#ffffff',
        text: 'Error' 
      },
      COMPLETE: { 
        backgroundColor: '#037f0c', 
        color: '#ffffff',
        text: 'Complete' 
      },
      INCOMPLETE: { 
        backgroundColor: '#5f6b7a', 
        color: '#ffffff',
        text: 'Incomplete' 
      }
    };

    const config = statusConfig[normalizedStatus] || { 
      backgroundColor: '#5f6b7a', 
      color: '#ffffff',
      text: status || 'Unknown' 
    };
    
    return (
      <Box
        display="inline-block"
        padding={{ vertical: 'xxs', horizontal: 'xs' }}
        fontSize="body-s"
        fontWeight="bold"
        style={{
          backgroundColor: config.backgroundColor,
          color: config.color,
          borderRadius: '4px',
          minWidth: '80px',
          textAlign: 'center'
        }}
      >
        {config.text}
      </Box>
    );
  };

  const filteredDocuments = documents.filter(doc =>
    doc.fileKey?.toLowerCase().includes(filteringText.toLowerCase()) ||
    doc.fileType?.toLowerCase().includes(filteringText.toLowerCase())
  );

  const paginatedDocuments = filteredDocuments.slice(
    (currentPageIndex - 1) * pageSize,
    currentPageIndex * pageSize
  );

  const columnDefinitions = [
    {
      id: 'displayName',
      header: 'Document Name',
      cell: item => item.displayName,
      sortingField: 'displayName'
    },
    {
      id: 'submissionId',
      header: 'Submission ID',
      cell: item => item.submissionId || '-',
      sortingField: 'submissionId'
    },
    {
      id: 'uploadTimestamp',
      header: 'Upload Time',
      cell: item => item.uploadTimestamp ? new Date(item.uploadTimestamp).toLocaleString() : '-',
      sortingField: 'uploadTimestamp'
    },
    {
      id: 'status',
      header: 'Status',
      cell: item => getStatusBadge(item.status)
    },
    {
      id: 'bedrockAnalysis',
      header: 'AI Analysis',
      cell: item => {
        if (!item.bedrockAnalysis) {
          return <Badge color="grey">Pending</Badge>;
        }
        const status = item.bedrockAnalysis.complianceStatus;
        if (status === 'COMPLIANT') {
          return <Badge color="green">✓ Compliant</Badge>;
        } else if (status === 'NON_COMPLIANT') {
          return <Badge color="red">✗ Non-Compliant</Badge>;
        } else {
          return <Badge color="blue">⚠ Review</Badge>;
        }
      }
    },
    {
      id: 'businessRulesScore',
      header: 'Rules Score',
      cell: item => {
        if (item.businessRulesScore === undefined) {
          return '-';
        }
        const score = Math.round(item.businessRulesScore);
        let color = 'grey';
        if (score >= 95) color = 'green';
        else if (score >= 80) color = 'blue';
        else if (score >= 70) color = 'red';
        else color = 'red';
        
        return <Badge color={color}>{score}%</Badge>;
      },
      sortingField: 'businessRulesScore'
    },
    {
      id: 'actions',
      header: 'Actions',
      cell: item => (
        <Button
          variant="inline-link"
          onClick={() => onSelectDocument(item)}
        >
          View Details
        </Button>
      )
    }
  ];

  return (
    <Container
      header={
        <Header
          variant="h2"
          description="View and manage form submissions"
          actions={
            <Button
              variant="primary"
              iconName="refresh"
              onClick={loadDocuments}
              loading={loading}
            >
              Refresh
            </Button>
          }
        >
          Form Submissions
        </Header>
      }
    >
      <SpaceBetween direction="vertical" size="l">
        {alert && (
          <Alert
            type={alert.type}
            dismissible
            onDismiss={() => setAlert(null)}
          >
            {alert.content}
          </Alert>
        )}

        <Table
          columnDefinitions={columnDefinitions}
          items={paginatedDocuments}
          loading={loading}
          loadingText="Loading documents..."
          selectedItems={selectedItems}
          onSelectionChange={({ detail }) => setSelectedItems(detail.selectedItems)}
          selectionType="single"
          header={
            <Header
              counter={`(${filteredDocuments.length})`}
              actions={
                <SpaceBetween direction="horizontal" size="xs">
                  <Button
                    disabled={selectedItems.length === 0}
                    onClick={() => onSelectDocument(selectedItems[0])}
                  >
                    View Selected
                  </Button>
                </SpaceBetween>
              }
            >
              Documents
            </Header>
          }
          filter={
            <TextFilter
              filteringText={filteringText}
              onChange={({ detail }) => setFilteringText(detail.filteringText)}
              filteringPlaceholder="Search documents..."
            />
          }
          pagination={
            <Pagination
              currentPageIndex={currentPageIndex}
              onChange={({ detail }) => setCurrentPageIndex(detail.currentPageIndex)}
              pagesCount={Math.ceil(filteredDocuments.length / pageSize)}
            />
          }
          empty={
            <Box textAlign="center" color="inherit">
              <b>No documents</b>
              <Box padding={{ bottom: 's' }} variant="p" color="inherit">
                No documents found. Upload a document to get started.
              </Box>
            </Box>
          }
        />
      </SpaceBetween>
    </Container>
  );
}

export default DocumentList;